import { Tooltip } from 'react-tooltip'
import { useFormattedExportStore } from './useFormattedExportStore'
import { IconHelpCircle } from '@tabler/icons-react'

export const TranscriptFormattedViewOptions = () => {
  const {
    setIncludeSpeaker,
    setFormat,
    format,
    includeSpeaker,
    inlineSpeakers,
    includeTimestamps,
    setIncludeTimestamps,
    setInlineSpeakers,
    inlineTimestamps,
    setInlineTimestamps,
    setSpaceBetweenSpeakers,
    spaceBetweenSpeakers,
    showSameSpeakerOnce,
    setShowSameSpeakerOnce,
  } = useFormattedExportStore()

  return (
    <fieldset>
      <legend className="text-lg font-semibold text-gray-900">
        Export Options
      </legend>
      <div className="mt-2 space-y-4">
        {/* Format Option */}
        <div>
          <span className="text-sm font-medium text-gray-900">Format:</span>
          <div className="mt-2 flex items-center space-x-4">
            <label className="flex items-center">
              <input
                onChange={() => setFormat('paragraphs')}
                type="radio"
                name="format"
                defaultChecked={format === 'paragraphs'}
                className="h-4 w-4 border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
                data-tooltip-id="format-paragraphs-tooltip"
              />
              <span className="ml-2 text-sm text-gray-900">Paragraphs</span>
            </label>
            <Tooltip
              variant="dark"
              content="Export text as continuous paragraphs."
              id="format-paragraphs-tooltip"
            />
            <label className="flex items-center">
              <input
                onChange={() => setFormat('sentences')}
                type="radio"
                name="format"
                defaultChecked={format === 'sentences'}
                className="h-4 w-4 border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
                data-tooltip-id="format-sentences-tooltip"
              />
              <span className="ml-2 text-sm text-gray-900">Sentences</span>
            </label>
            <Tooltip
              variant="dark"
              content="Export text as individual sentences."
              id="format-sentences-tooltip"
            />
          </div>
        </div>

        {/* Combine Speaker Segments */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              id="show-same-speaker-once"
              type="checkbox"
              defaultChecked={showSameSpeakerOnce}
              onChange={(e) => setShowSameSpeakerOnce(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
              data-tooltip-id="show-same-speaker-once-tooltip"
            />
            <label
              htmlFor="show-same-speaker-once"
              className="ml-3 text-sm text-gray-900"
            >
              Combine Speaker Segments
            </label>
          </div>
          <Tooltip
            variant="dark"
            content="Group all sentences by the same speaker together."
            id="show-same-speaker-once-tooltip"
          />
        </div>

        {/* Include Speaker Labels Option */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              onChange={(e) => setIncludeSpeaker(e.target.checked)}
              id="include-speaker"
              type="checkbox"
              defaultChecked={includeSpeaker}
              className="h-4 w-4 rounded border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
            />
            <label
              htmlFor="include-speaker"
              className="ml-3 text-sm text-gray-900"
            >
              Include Speaker Labels
            </label>
          </div>
        </div>

        {/* Inline Speakers Option with Tooltip */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              id="inline-speakers"
              type="checkbox"
              defaultChecked={inlineSpeakers}
              onChange={(e) => setInlineSpeakers(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
              data-tooltip-id="inline-speakers-tooltip"
            />
            <label
              htmlFor="inline-speakers"
              className="ml-3 text-sm text-gray-900"
            >
              Inline Speakers
            </label>
          </div>
          <Tooltip
            variant="dark"
            content="Display speaker names within the same line as their speech for a compact view."
            id="inline-speakers-tooltip"
          />
        </div>

        {/* Include Timestamps Option */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              id="include-timestamps"
              type="checkbox"
              defaultChecked={includeTimestamps}
              onChange={(e) => setIncludeTimestamps(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
            />
            <label
              htmlFor="include-timestamps"
              className="ml-3 text-sm text-gray-900"
            >
              Include Timestamps
            </label>
          </div>
        </div>

        {/* Inline Timestamps Option with Tooltip */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              id="inline-timestamps"
              type="checkbox"
              defaultChecked={inlineTimestamps}
              onChange={(e) => setInlineTimestamps(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
              data-tooltip-id="inline-timestamps-tooltip"
            />
            <label
              htmlFor="inline-timestamps"
              className="ml-3 text-sm text-gray-900"
            >
              Inline Timestamps
            </label>
          </div>
          <Tooltip
            variant="dark"
            content="Place timestamps next to the text they correspond to for easier reference."
            id="inline-timestamps-tooltip"
          />
        </div>

        {/* Space Between Speakers Option */}
        <div className="relative flex items-start">
          <div className="flex h-6 items-center">
            <input
              id="space-between-speakers"
              type="checkbox"
              defaultChecked={spaceBetweenSpeakers}
              onChange={(e) => setSpaceBetweenSpeakers(e.target.checked)}
              checked={spaceBetweenSpeakers}
              className="h-4 w-4 rounded border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
              data-tooltip-id="space-between-speakers-tooltip"
            />
            <label
              htmlFor="space-between-speakers"
              className="ml-3 text-sm text-gray-900"
            >
              Space Between Speakers
            </label>
          </div>
          <Tooltip
            variant="dark"
            content="Add a blank line between each speaker's speech."
            id="space-between-speakers-tooltip"
          />
        </div>
      </div>
    </fieldset>
  )
}
